// IndexedDB mínimo sobre la API cruda (molde de Carpenter: sin la librería `idb`,
// una dependencia menos). Dos stores en la misma base:
//   · rqcache → el cache persistido de react-query (una sola entrada)
//   · drafts  → los borradores del editor (una entrada por borrador)
//
// TOLERANCIA A FALLOS: en modo privado, con la cuota llena o con IndexedDB
// bloqueado por política, TODO aquí degrada a no-op en vez de romper la app. Es el
// mismo criterio que ya usa TabBar con sessionStorage. Nunca lanza.

const DB_NAME = 'bee-camp-offline'
const DB_VERSION = 1
export const STORE_CACHE = 'rqcache'
export const STORE_DRAFTS = 'drafts'

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null)
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      return resolve(null)
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_CACHE)) db.createObjectStore(STORE_CACHE)
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) db.createObjectStore(STORE_DRAFTS)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
  return dbPromise
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null)
        try {
          const tx = db.transaction(store, mode)
          const req = fn(tx.objectStore(store))
          req.onsuccess = () => resolve(req.result as T)
          req.onerror = () => resolve(null)
          tx.onabort = () => resolve(null)
        } catch {
          resolve(null)
        }
      }),
  )
}

export const idbGet = <T>(store: string, key: string) => run<T>(store, 'readonly', (s) => s.get(key))
export const idbSet = (store: string, key: string, val: unknown) =>
  run(store, 'readwrite', (s) => s.put(val, key))
export const idbDel = (store: string, key: string) => run(store, 'readwrite', (s) => s.delete(key))
export const idbKeys = (store: string) => run<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys())
export const idbClear = (store: string) => run(store, 'readwrite', (s) => s.clear())
